import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import { isReducedMotionEnabled } from "@constants";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import type { PanDirection } from "./densityViewport.lib";

const EDGE_ZONE_PX = 40;
const MAX_SPEED_PX_PER_MS = 0.8;
// Three time constants: 95% of the way to a new speed after about 100ms.
const RAMP_TIME_CONSTANT_MS = 100 / 3;
const REDUCED_MOTION_STEP_MS = 600;
const REDUCED_MOTION_STEP_SHARE = 0.8;

/** How deep `clientX` is in an edge zone: 0 outside both, up to -1 at or past the left edge and 1 at or past the right. */
const toEdgeDepth = (clientX: number, { left, right }: DOMRect) => {
    const intoLeft = (left + EDGE_ZONE_PX - clientX) / EDGE_ZONE_PX;
    const intoRight = (clientX - right + EDGE_ZONE_PX) / EDGE_ZONE_PX;
    if (intoLeft > 0) {
        return -Math.min(intoLeft, 1);
    }
    return intoRight > 0 ? Math.min(intoRight, 1) : 0;
};

interface UseEdgePanParams {
    /** The visible area whose edges start the pan. */
    edgeRef: RefObject<HTMLElement | null>;
    /** Pans by px, negative for earlier. */
    onEdgePan: (px: number) => void;
    /** Whether panning that way still moves what's dragged. */
    canPanToward: (direction: PanDirection) => boolean;
}

/**
 * Pans while a dragged pointer is within 40px of the visible area's edges or
 * past them, every frame, faster the deeper it is, easing into each new
 * speed so entering the zone doesn't jerk, and stops once panning that way
 * can't move what's dragged. With reduced motion it jumps most of a screen
 * every 600ms instead of scrolling.
 */
export function useEdgePan({
    edgeRef,
    onEdgePan,
    canPanToward,
}: UseEdgePanParams) {
    const frameRef = useRef<number | null>(null);
    const clientXRef = useRef(0);
    const latestRef = useLatestRef({ onEdgePan, canPanToward });

    const stop = useCallback(() => {
        if (frameRef.current !== null) {
            window.cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
    }, []);
    useEffect(() => stop, [stop]);

    const depthAt = useCallback(() => {
        const rect = edgeRef.current?.getBoundingClientRect();
        const depth = rect ? toEdgeDepth(clientXRef.current, rect) : 0;
        return depth !== 0 &&
            latestRef.current.canPanToward(depth < 0 ? "earlier" : "later")
            ? depth
            : 0;
    }, [edgeRef, latestRef]);

    /** Follows the dragged pointer, starting the pan when it enters an edge zone. */
    const follow = useCallback(
        (clientX: number) => {
            clientXRef.current = clientX;
            if (frameRef.current !== null || depthAt() === 0) {
                return;
            }
            let lastTime: number | null = null;
            let sinceStep = 0;
            let speed = 0;
            const tick = (time: number) => {
                const depth = depthAt();
                const width = edgeRef.current?.clientWidth ?? 0;
                if (depth === 0) {
                    frameRef.current = null;
                    return;
                }
                const elapsed = lastTime === null ? 0 : time - lastTime;
                lastTime = time;
                if (!isReducedMotionEnabled) {
                    const target =
                        depth * Math.abs(depth) * MAX_SPEED_PX_PER_MS;
                    speed +=
                        (target - speed) *
                        (1 - Math.exp(-elapsed / RAMP_TIME_CONSTANT_MS));
                    latestRef.current.onEdgePan(speed * elapsed);
                } else if (sinceStep + elapsed >= REDUCED_MOTION_STEP_MS) {
                    sinceStep = 0;
                    latestRef.current.onEdgePan(
                        Math.sign(depth) * width * REDUCED_MOTION_STEP_SHARE,
                    );
                } else {
                    sinceStep += elapsed;
                }
                frameRef.current = window.requestAnimationFrame(tick);
            };
            frameRef.current = window.requestAnimationFrame(tick);
        },
        [depthAt, edgeRef, latestRef],
    );

    return { follow, stop };
}
